import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { cn } from '@/utils/tailwind.util';
import { usePdfPageDetailPreview } from '../hooks/usePdfPageDetailPreview';
import { type AiBuilderChatCopy } from '../utils/aiBuilderChatCopy';
import { type PdfPageRange, rangesToPageNumbers } from '../utils/pdfPageRanges';
import { openPdfPreview, type PdfPreviewDocument, type PdfThumbnail } from '../utils/pdfPreviewAdapter';
import { createPdfThumbnailQueue, type PdfThumbnailQueue } from '../utils/pdfThumbnailQueue';
import { AiBuilderAttachmentPreviewCard } from './AiBuilderAttachmentPreviewCard';
const isCancelledError = (error: unknown) => error instanceof DOMException && error.name === 'AbortError' ? true : typeof error === 'object' && error !== null && 'code' in error && error.code === 'cancelled';
type PreviewState = {
  status: 'idle';
} | {
  status: 'loading';
} | {
  status: 'ready';
  preview: PdfPreviewDocument;
  queue: PdfThumbnailQueue;
} | {
  status: 'failed';
};
type PdfPageCardProps = {
  copy: AiBuilderChatCopy['attachmentPreview'];
  loadingDetail: boolean;
  onPreview: (fallbackImageSrc?: string) => void;
  onRemove?: () => void;
  pageNumber: number;
  previewState: Extract<PreviewState, {
    status: 'ready';
  }> | null;
  size: 'composer' | 'message';
};
const PdfPageCard = ({
  copy,
  loadingDetail,
  onPreview,
  onRemove,
  pageNumber,
  previewState,
  size
}: PdfPageCardProps) => {
  const {
    ref,
    inView
  } = useInView({
    rootMargin: '0px 120px',
    triggerOnce: true
  });
  const [thumbnail, setThumbnail] = useState<PdfThumbnail | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!inView || !previewState || thumbnail || failed) return;
    const controller = new AbortController();
    void previewState.queue.run(() => previewState.preview.renderThumbnail(pageNumber, {
      maxWidth: size === 'composer' ? 148 : 180,
      signal: controller.signal
    }), controller.signal).then(setThumbnail).catch(error => {
      if (!isCancelledError(error)) setFailed(true);
    });
    return () => controller.abort();
  }, [failed, inView, pageNumber, previewState, size, thumbnail]);
  return <li ref={ref} data-sentry-component="PdfPageCard" data-sentry-source-file="AiBuilderPdfPagePreviewList.tsx">
      <AiBuilderAttachmentPreviewCard badge={copy.pageBadge(pageNumber)} loading={loadingDetail || previewState !== null && !thumbnail && !failed} {...previewState ? {
      onPreview: () => onPreview(thumbnail?.dataUrl),
      previewAriaLabel: copy.pagePreview(pageNumber)
    } : {}} {...onRemove ? {
      onRemove,
      removeAriaLabel: copy.pageRemove(pageNumber)
    } : {}} size={size} data-sentry-element="AiBuilderAttachmentPreviewCard" data-sentry-source-file="AiBuilderPdfPagePreviewList.tsx">
        {thumbnail ? <img src={thumbnail.dataUrl} alt="" className="size-full object-cover object-top" /> : <span className="text-caption-md-semibold text-neutral">
            {pageNumber}
          </span>}
      </AiBuilderAttachmentPreviewCard>
    </li>;
};
type AiBuilderPdfPagePreviewListProps = {
  className?: string;
  copy: AiBuilderChatCopy['attachmentPreview'];
  file: File;
  onRemovePage?: (pageNumber: number) => void;
  openPreview?: typeof openPdfPreview;
  pageCount: number;
  pageRanges: readonly PdfPageRange[];
  size: 'composer' | 'message';
};

/** PDF를 한 번 열고 선택된 페이지 카드들의 썸네일과 상세 이미지를 공급한다. */
export const AiBuilderPdfPagePreviewList = ({
  className,
  copy,
  file,
  onRemovePage,
  openPreview = openPdfPreview,
  pageCount,
  pageRanges,
  size
}: AiBuilderPdfPagePreviewListProps) => {
  const pageNumbers = useMemo(() => rangesToPageNumbers(pageRanges).filter(page => page <= pageCount), [pageCount, pageRanges]);
  const {
    ref: listRef,
    inView: listInView
  } = useInView({
    rootMargin: '300px 0px',
    triggerOnce: true
  });
  const shouldOpenPreview = size === 'composer' || listInView;
  const [previewState, setPreviewState] = useState<PreviewState>({
    status: 'idle'
  });
  const readyPreviewState = previewState.status === 'ready' ? previewState : null;
  const {
    loadingPage: detailLoadingPage,
    openPageDetail
  } = usePdfPageDetailPreview(readyPreviewState?.preview ?? null);
  useEffect(() => {
    if (!shouldOpenPreview) {
      setPreviewState({
        status: 'idle'
      });
      return;
    }
    const controller = new AbortController();
    const queue = createPdfThumbnailQueue(2);
    let preview: PdfPreviewDocument | null = null;
    setPreviewState({
      status: 'loading'
    });
    void openPreview(file, {
      signal: controller.signal
    }).then(document => {
      if (controller.signal.aborted) {
        void document.destroy();
        return;
      }
      preview = document;
      setPreviewState({
        status: 'ready',
        preview: document,
        queue
      });
    }).catch(() => {
      if (!controller.signal.aborted) setPreviewState({
        status: 'failed'
      });
    });
    return () => {
      controller.abort();
      queue.cancel();
      if (preview) void preview.destroy();
    };
  }, [file, openPreview, shouldOpenPreview]);
  return <ol ref={listRef} aria-label={copy.selectedPagesAriaLabel} className={cn('flex gap-sm', size === 'composer' ? 'w-max min-w-full' : 'flex-wrap', className)} data-sentry-component="AiBuilderPdfPagePreviewList" data-sentry-source-file="AiBuilderPdfPagePreviewList.tsx">
      {pageNumbers.map(pageNumber => <PdfPageCard key={pageNumber} copy={copy} loadingDetail={detailLoadingPage === pageNumber} onPreview={fallback => void openPageDetail(pageNumber, fallback)} onRemove={onRemovePage ? () => onRemovePage(pageNumber) : undefined} pageNumber={pageNumber} previewState={readyPreviewState} size={size} />)}
    </ol>;
};